import {
    Activity,
    BookOpen,
    Bot,
    Cpu,
    LayoutDashboard,
    LayoutList,
    LogOut,
    type LucideIcon,
    MessagesSquare,
    Moon,
    ScrollText,
    Search,
    ShieldCheck,
    Sparkles,
    Sun,
    UploadCloud,
    Users,
    Wrench,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KNOKA_LOGOUT_URL } from '~/api/auth'
import { listCatalogItems } from '~/api/catalog'
import type { CatalogItem } from '~/api/types'
import { Spinner } from '~/components/ui/spinner'
import { applyTheme, getStoredTheme } from '~/lib/theme'
import { useCommandPalette } from '~/lib/use-command-palette'
import { cn } from '~/lib/utils'

// ⌘K / Ctrl+K 전역 커맨드 팔레트(Esc 닫기). 모달 오버레이(중앙 상단, 미니멀).
// 섹션: 이동(라우트)·작업(테마/로그아웃)·검색(카탈로그, 300ms 디바운스).
// cmdk 미사용 → React 상태 + keydown 핸들러로 직접 구현(↑↓ 이동·Enter 실행·hover).

type CommandItem = {
    id: string
    label: string
    hint?: string
    icon: LucideIcon
    run: () => void
}

type CommandSection = { heading: string; items: CommandItem[] }

// 이동 대상 라우트(LNB와 동일한 IA).
const ROUTES: { to: string; label: string; icon: LucideIcon }[] = [
    { to: '/', label: '운영 대시보드', icon: LayoutDashboard },
    { to: '/consultation', label: '상담', icon: MessagesSquare },
    { to: '/certification', label: '인증평가', icon: ShieldCheck },
    { to: '/maintenance', label: '유지하기', icon: Wrench },
    { to: '/monitoring', label: '모니터링', icon: Activity },
    { to: '/governance/models', label: '모델', icon: Cpu },
    { to: '/governance/prompts', label: '프롬프트', icon: Sparkles },
    { to: '/governance/agents', label: '에이전트 설정', icon: Bot },
    { to: '/content/catalog', label: '카탈로그', icon: LayoutList },
    { to: '/content/knowledge', label: '지식베이스', icon: BookOpen },
    { to: '/content/uploads', label: '업로드 관리', icon: UploadCloud },
    { to: '/users', label: '사용자/조직', icon: Users },
    { to: '/audit', label: '감사로그', icon: ScrollText },
]

export function CommandPalette() {
    const open = useCommandPalette((s) => s.open)
    const setOpen = useCommandPalette((s) => s.setOpen)
    const navigate = useNavigate()

    const [query, setQuery] = useState('')
    const [active, setActive] = useState(0)
    const [results, setResults] = useState<CatalogItem[]>([])
    const [searching, setSearching] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    // 전역 단축키: ⌘K / Ctrl+K 토글.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault()
                useCommandPalette.getState().toggle()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    // 열릴 때 입력 초기화 + 포커스. 닫히면 검색 상태도 정리.
    useEffect(() => {
        if (open) {
            setQuery('')
            setResults([])
            setActive(0)
            // 다음 틱에 포커스(오버레이 마운트 후).
            requestAnimationFrame(() => inputRef.current?.focus())
        }
    }, [open])

    // 카탈로그 검색(300ms 디바운스). 입력어가 있을 때만.
    useEffect(() => {
        if (!open) return
        const q = query.trim()
        if (!q) {
            setResults([])
            setSearching(false)
            return
        }
        setSearching(true)
        const handle = setTimeout(() => {
            listCatalogItems({ q, limit: 6 })
                .then((rows) => setResults(rows))
                .catch(() => setResults([]))
                .finally(() => setSearching(false))
        }, 300)
        return () => clearTimeout(handle)
    }, [query, open])

    const close = useCallback(() => setOpen(false), [setOpen])

    const sections = useMemo<CommandSection[]>(() => {
        const q = query.trim().toLowerCase()

        const navItems: CommandItem[] = ROUTES.filter(
            (r) => !q || r.label.toLowerCase().includes(q)
        ).map((r) => ({
            id: `nav:${r.to}`,
            label: r.label,
            hint: '이동',
            icon: r.icon,
            run: () => {
                navigate(r.to)
                close()
            },
        }))

        const isDark = getStoredTheme() === 'dark'
        const actionDefs: CommandItem[] = [
            {
                id: 'action:theme',
                label: isDark ? '라이트 모드로 전환' : '다크 모드로 전환',
                hint: '작업',
                icon: isDark ? Sun : Moon,
                run: () => {
                    applyTheme(isDark ? 'light' : 'dark')
                    close()
                },
            },
            {
                id: 'action:logout',
                label: '로그아웃',
                hint: '작업',
                icon: LogOut,
                run: () => {
                    // 서버사이드 흐름(쿠키·세션 정리 + knoka end_session)으로 전체 페이지 이동.
                    window.location.href = KNOKA_LOGOUT_URL
                },
            },
        ]
        const actionItems = actionDefs.filter(
            (a) => !q || a.label.toLowerCase().includes(q)
        )

        const searchItems: CommandItem[] = results.map((item) => ({
            id: `catalog:${item.id}`,
            label: item.title,
            hint: item.part ? `PART ${item.part}` : '카탈로그',
            icon: LayoutList,
            run: () => {
                navigate(
                    `/content/catalog?highlight=${encodeURIComponent(item.id)}`
                )
                close()
            },
        }))

        const out: CommandSection[] = []
        if (navItems.length) out.push({ heading: '이동', items: navItems })
        if (actionItems.length)
            out.push({ heading: '작업', items: actionItems })
        if (query.trim())
            out.push({ heading: '검색 (카탈로그)', items: searchItems })
        return out
    }, [query, results, navigate, close])

    // 평탄화된 실행 가능 항목(↑↓/Enter 대상).
    const flat = useMemo(() => sections.flatMap((s) => s.items), [sections])

    // 결과가 바뀌면 활성 인덱스를 범위 안으로 보정.
    useEffect(() => {
        setActive((i) => (i >= flat.length ? 0 : i))
    }, [flat.length])

    // 활성 항목이 보이도록 스크롤.
    useEffect(() => {
        const el = listRef.current?.querySelector<HTMLElement>(
            '[data-active="true"]'
        )
        el?.scrollIntoView({ block: 'nearest' })
    }, [active])

    if (!open) return null

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            close()
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => (flat.length ? (i + 1) % flat.length : 0))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) =>
                flat.length ? (i - 1 + flat.length) % flat.length : 0
            )
        } else if (e.key === 'Enter') {
            e.preventDefault()
            flat[active]?.run()
        }
    }

    let runningIndex = -1

    return (
        <div
            className='fixed inset-0 z-[90] flex items-start justify-center px-4 pt-[12vh]'
            onKeyDown={onKeyDown}>
            {/* 배경 오버레이 — 클릭 시 닫힘 */}
            <button
                type='button'
                aria-label='닫기'
                tabIndex={-1}
                className='bg-foreground/40 absolute inset-0'
                onClick={close}
            />
            <div
                role='dialog'
                aria-modal='true'
                aria-label='명령 팔레트'
                className='border-border bg-popover text-popover-foreground relative w-full max-w-xl overflow-hidden rounded-[var(--radius-lg)] border shadow-sm'>
                {/* 입력창 */}
                <div className='border-border flex items-center gap-2.5 border-b px-3'>
                    <Search className='text-muted-foreground size-4 shrink-0' />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder='검색 또는 이동…'
                        aria-label='명령 검색'
                        className='text-foreground placeholder:text-muted-foreground h-11 w-full bg-transparent text-[13px] outline-none'
                    />
                    {searching ? <Spinner size='sm' /> : null}
                </div>

                {/* 결과 리스트 */}
                <div
                    ref={listRef}
                    role='listbox'
                    className='max-h-80 overflow-y-auto p-1.5'>
                    {flat.length === 0 ? (
                        <p className='text-muted-foreground px-2.5 py-6 text-center text-[13px]'>
                            {searching ? '검색 중…' : '결과가 없어요.'}
                        </p>
                    ) : (
                        sections.map((section) => (
                            <div key={section.heading} className='mb-1'>
                                <div className='ui-label px-2.5 pt-2 pb-1'>
                                    {section.heading}
                                </div>
                                {section.items.length === 0 &&
                                section.heading.startsWith('검색') ? (
                                    <p className='text-muted-foreground px-2.5 py-2 text-[12px]'>
                                        {searching
                                            ? '검색 중…'
                                            : '일치하는 항목이 없어요.'}
                                    </p>
                                ) : (
                                    section.items.map((item) => {
                                        runningIndex += 1
                                        const idx = runningIndex
                                        const isActive = idx === active
                                        const Icon = item.icon
                                        return (
                                            <button
                                                key={item.id}
                                                type='button'
                                                role='option'
                                                aria-selected={isActive}
                                                data-active={isActive}
                                                onMouseMove={() =>
                                                    setActive(idx)
                                                }
                                                onClick={item.run}
                                                className={cn(
                                                    'flex w-full items-center gap-2.5 rounded-[var(--radius)] px-2.5 py-2 text-left text-[13px] outline-none',
                                                    isActive
                                                        ? 'bg-accent text-accent-foreground'
                                                        : 'text-foreground'
                                                )}>
                                                <Icon className='text-muted-foreground size-4 shrink-0' />
                                                <span className='min-w-0 flex-1 truncate'>
                                                    {item.label}
                                                </span>
                                                {item.hint ? (
                                                    <span className='text-muted-foreground shrink-0 text-[11px]'>
                                                        {item.hint}
                                                    </span>
                                                ) : null}
                                            </button>
                                        )
                                    })
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}
